import { format, parseISO, isValid } from "date-fns";

// Format number to k format (e.g., 250000 -> 250k)
export const formatToK = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return value.toString();
};

// Format number with thousand separators using period (e.g., 1000000 -> 1.000.000)
export const formatNumber = (value: number | string): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return "0";
  }
  return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/**
 * Format a date/timestamp to a human-readable format.
 * Converts ISO 8601 timestamps to "DD MMM YYYY HH:mm:ss" format in local timezone.
 *
 * @param dateValue - ISO 8601 string, Date object, or timestamp number
 * @param fallback - Value to return if date is invalid (default: "-")
 * @returns Formatted date string (e.g., "02 Dec 2024 15:23:42") or fallback
 *
 * @example
 * formatDateTime("2025-12-02T09:06:17.156111Z") // "02 Dec 2024 09:06:17"
 * formatDateTime(new Date()) // current date/time formatted
 * formatDateTime(null) // "-"
 */
export const formatDateTime = (
  dateValue: string | Date | number | null | undefined,
  fallback: string = "-",
): string => {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return fallback;
  }

  try {
    let date: Date;
    if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }

    if (!isValid(date)) {
      return fallback;
    }

    return format(date, "dd MMM yyyy HH:mm:ss");
  } catch {
    return fallback;
  }
};

export const formatDate = (
  dateValue: string | Date | number | null | undefined,
  fallback: string = "-",
): string => {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return fallback;
  }

  try {
    let date: Date;
    if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }

    if (!isValid(date)) {
      return fallback;
    }

    return format(date, "dd MMM yyyy");
  } catch {
    return fallback;
  }
};

/**
 * Convert a number to string for form input display.
 * Returns empty string for 0 to allow natural input behavior.
 *
 * @param num - The number to convert
 * @returns String representation, or empty string if 0
 *
 * @example
 * numberToString(123) // "123"
 * numberToString(0) // ""
 */
export const numberToString = (num: number): string => {
  return num === 0 ? "" : String(num);
};

/**
 * Convert a string to number for form state storage.
 * Returns 0 for empty string to maintain numeric type.
 *
 * @param str - The string to convert
 * @returns Numeric value, or 0 if empty string
 *
 * @example
 * stringToNumber("123") // 123
 * stringToNumber("") // 0
 */
export const stringToNumber = (str: string): number => {
  return str === "" ? 0 : Number(str);
};